import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

type UseResendEmailVerificationActionData = {
    resendEmailVerification: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Sending the verification email again.
 *
 * Carries no `isAllowed`: it acts on the signed-in user's own address, so
 * there is nobody who can reach it and may not use it.
 */
export function useResendEmailVerificationAction(): UseResendEmailVerificationActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/me/email/resend` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'EMAIL_ALREADY_VERIFIED')) {
                return t(
                    'actions.resend_email_verification.error_already_verified'
                );
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.me.resendEmailVerification().then(() => {}),
        // Nothing to write to the cache: the address is unchanged until the
        // link in the email is followed.
        onSuccess: () => {
            UIToast.success(t('actions.resend_email_verification.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        resendEmailVerification: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
